import type { OriginalInfo } from '../../../generated';
import type { TypePolicies } from '../../../utils';
import { isMediaUrlDifferentOrExpiring, transcodeUrlMerge } from '../utils';

/**
 * Ensures valid, un-expired download URLs are preferred over newly signed URLs.
 */
const originalInfoUrlKeys = ['downloadUrl', 'inlineUrl'] satisfies Array<
  keyof OriginalInfo
>;

const originalInfoMerge = (
  existing: OriginalInfo | null | undefined,
  incoming: OriginalInfo | null | undefined
) => {
  if (!existing || !incoming) return incoming;
  const mustUseIncoming = originalInfoUrlKeys.some((key) => {
    const existingUrl = existing[key];
    const incomingUrl = incoming[key];
    if (!existingUrl || !incomingUrl) return true;
    return isMediaUrlDifferentOrExpiring({
      current: existingUrl,
      next: incomingUrl,
      param: 'Expires',
    });
  });
  return mustUseIncoming ? incoming : existing;
};

const hlsManifestUrlMerge = (
  existing: string | null | undefined,
  incoming: string | null | undefined
) => {
  if (!existing || !incoming) return incoming;
  const shouldUseIncoming = isMediaUrlDifferentOrExpiring({
    current: existing,
    next: incoming,
    param: 'token',
  });
  return shouldUseIncoming ? incoming : existing;
};

export const typePolicies: TypePolicies = {
  ImageAsset: {
    fields: {
      media: {
        merge: true,
      },
    },
  },
  AudioAsset: {
    fields: {
      media: {
        merge: true,
      },
    },
  },
  VideoAsset: {
    fields: {
      media: {
        merge: true,
      },
    },
  },
  ModelAsset: {
    fields: {
      media: {
        merge: true,
      },
    },
  },
  DocumentAsset: {
    fields: {
      media: {
        merge: true,
      },
    },
  },
  InteractiveAsset: {
    fields: {
      media: {
        merge: true,
      },
    },
  },
  InteractiveLiveAsset: {
    fields: {
      media: {
        merge: true,
      },
    },
  },
  UnsupportedAsset: {
    fields: {
      media: {
        merge: true,
      },
    },
  },
  InteractiveMedia: {
    fields: {
      original: {
        merge: originalInfoMerge,
      },
    },
  },
  VideoMedia: {
    // Don't use the default behavior to allow for unique hls manifest URLs for captions
    keyFields: false,

    fields: {
      hlsManifest: {
        merge: hlsManifestUrlMerge,
      },
      original: {
        merge: originalInfoMerge,
      },
      imageTranscodes: {
        merge: transcodeUrlMerge,
      },
      videoTranscodes: {
        merge: transcodeUrlMerge,
      },
      thumbTranscodes: {
        merge: transcodeUrlMerge,
      },
    },
  },
  ModelMedia: {
    fields: {
      hlsManifest: {
        merge: hlsManifestUrlMerge,
      },
      original: {
        merge: originalInfoMerge,
      },
      imageTranscodes: {
        merge: transcodeUrlMerge,
      },
      videoTranscodes: {
        merge: transcodeUrlMerge,
      },
      modelTranscodes: {
        merge: transcodeUrlMerge,
      },
    },
  },
  AudioMedia: {
    fields: {
      hlsManifest: {
        merge: hlsManifestUrlMerge,
      },
      original: {
        merge: originalInfoMerge,
      },
      audioTranscodes: {
        merge: transcodeUrlMerge,
      },
      thumbTranscodes: {
        merge: transcodeUrlMerge,
      },
    },
  },
  DocumentMedia: {
    fields: {
      original: {
        merge: originalInfoMerge,
      },
      thumbTranscodes: {
        merge: transcodeUrlMerge,
      },
    },
  },
  ImageMedia: {
    fields: {
      original: {
        merge: originalInfoMerge,
      },
      imageTranscodes: {
        merge: transcodeUrlMerge,
      },
      thumbTranscodes: {
        merge: transcodeUrlMerge,
      },
    },
  },
  UnsupportedMedia: {
    fields: {
      original: {
        merge: originalInfoMerge,
      },
    },
  },
};
